import { TFunction } from 'i18next'
import { z } from 'zod'

import type { PackageSettings } from '@/models'

export type TabKey = 'general' | 'package-room' | 'locations' | 'carriers'

export type LocationItem = {
  id: string | number
  name: string
  packageRoom?: boolean
}

export type CarrierItem = {
  name: string
}

export type PackageRoomTabProps = {
  settings: PackageSettings
  isRegenerating: boolean
  onCopyToClipboard: (url: string) => void
  onRegenerateLink: (packageRoomId?: number) => void
}

export type PackagesSettingsProps = {
  onClose: () => void
  buildingId: number
  isPackagesD2DAvailable: boolean
  isQuickScanAvailable: boolean
}

export const MAX_DELIVERIES_PER_SLOT = 1_000
export const MAX_LOCATION_NAME_LENGTH = 50

const businessHourSchema = z.object({
  enabled: z.boolean(),
  endTime: z.number(),
  name: z.string(),
  startTime: z.number(),
})

const makeLocationItemSchema = (t: TFunction<['packages-settings']>) =>
  z.object({
    id: z.union([z.string(), z.number()]),
    name: z.string().max(
      MAX_LOCATION_NAME_LENGTH,
      t('packages-settings:locationsTab.validation.nameMaxLength', {
        max: MAX_LOCATION_NAME_LENGTH,
      }),
    ),
    packageRoom: z.boolean().optional(),
  }) as z.ZodType<LocationItem>

const carrierItemSchema = z.object({
  name: z.string(),
})

const makeCarriersArraySchema = (t: TFunction<['packages-settings']>) =>
  z.array(carrierItemSchema).superRefine((carriers, ctx) => {
    const seen = new Set<string>()

    carriers.forEach((carrier, index) => {
      const normalized = carrier.name.trim().toLowerCase()

      if (seen.has(normalized)) {
        ctx.addIssue({
          code: 'custom',
          message: t('packages-settings:carriersTab.validation.duplicateName'),
          path: [index, 'name'],
        })
      }

      seen.add(normalized)
    })
  })

export const makeNewCarrierNameSchema = (
  t: TFunction<['packages-settings']>,
  existingCarriers: CarrierItem[],
) =>
  z
    .string()
    .refine((name) => !existingCarriers.some((c) => c.name.toLowerCase() === name.toLowerCase()), {
      message: t('packages-settings:carriersTab.validation.duplicateName'),
    })

export const makePackagesSettingsFormSchema = (t: TFunction<['packages-settings']>) =>
  z
    .object({
      allowASAP: z.boolean(),
      allowQuickScan: z.boolean(),
      businessHours: z.array(businessHourSchema),
      carrierAccessCodeEnabled: z.boolean(),
      carriers: makeCarriersArraySchema(t),
      customLocationsEnabled: z.boolean(),
      d2dMode: z.boolean(),
      deliverySlotLength: z.number(),
      locationRequired: z.boolean(),
      locations: z.array(makeLocationItemSchema(t)),
      maskUsernameEnabled: z.boolean(),
      maxDeliveriesPerSlot: z.preprocess(
        (val) => (val === '' ? undefined : val),
        z.coerce
          .number()
          .max(
            MAX_DELIVERIES_PER_SLOT,
            t('packages-settings:generalTab.maxDeliveriesPerSlot.validation.maxExceeded', {
              max: MAX_DELIVERIES_PER_SLOT,
            }),
          )
          .optional(),
      ),
      photoRequired: z.boolean(),
      trackingNumberRequired: z.boolean(),
    })
    .superRefine((data, ctx) => {
      if (data.customLocationsEnabled) {
        const filled = data.locations.filter((l) => l.name.trim() !== '')
        if (filled.length === 0) {
          ctx.addIssue({
            code: 'custom',
            message: t('packages-settings:locationsTab.validation.atLeastOneLocation'),
            path: ['locations'],
          })
        }
      }
      if (data.locationRequired && !data.customLocationsEnabled) {
        ctx.addIssue({
          code: 'custom',
          message: t(
            'packages-settings:locationsTab.validation.customLocationsRequiredForLocationRequired',
          ),
          path: ['locationRequired'],
        })
      }
    })

export type PackagesSettingsForm = z.infer<ReturnType<typeof makePackagesSettingsFormSchema>>
